<script lang="js">
import CTA from "@/components/CTA.vue";
import axios from "axios";

export default {
  name: "CareersView",
  components: { CTA },
  data() {
    return {
      logger: null,
      jobs: [],
      loading: true,
      searchQuery: "",
      selectedType: "",
      employmentTypes: ["Full-Time", "Part-Time", "Contract", "Temporary", "Internship"],
    };
  },
  created() {
    this.logger = this.$createLogger("CareersView");
  },
  mounted() {
    this.fetchActiveJobs();
  },
  methods: {
    async fetchActiveJobs() {
      try {
        this.loading = true;
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/jobs/active`
        );
        if (response.data.success) {
          this.jobs = response.data.data;
        }
      } catch (error) {
        this.logger.error("Error fetching jobs:", error);
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-NG", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
    getTypeColor(type) {
      const colors = {
        "Full-Time": "primary",
        "Part-Time": "info",
        "Contract": "warning",
        "Temporary": "secondary",
        "Internship": "success",
      };
      return colors[type] || "secondary";
    },
    getLocationIcon(location) {
      const icons = {
        "On-Site": "bi-building",
        "Remote": "bi-laptop",
        "Hybrid": "bi-arrows-angle-expand",
      };
      return icons[location] || "bi-geo-alt";
    },
  },
  computed: {
    filteredJobs() {
      let filtered = this.jobs;

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(
          (job) =>
            job.title.toLowerCase().includes(query) ||
            (job.department && job.department.toLowerCase().includes(query)) ||
            job.tags.some((tag) => tag.toLowerCase().includes(query))
        );
      }

      if (this.selectedType) {
        filtered = filtered.filter(
          (job) => job.employmentType === this.selectedType
        );
      }

      return filtered;
    },
    allTags() {
      const tags = new Set();
      this.jobs.forEach((job) => {
        job.tags.forEach((tag) => tags.add(tag));
      });
      return Array.from(tags);
    },
  },
};
</script>

<template>
  <div class="careers-page">
    <!-- Hero Banner -->
    <div class="bg-image-overlay">
      <div class="overlay"></div>
      <div class="content">
        <h1 v-scrollanimation.once="'fadeInUp'">Careers</h1>
        <p class="lead mt-3" v-scrollanimation.once="'fadeInUp'">
          Join our team and make a difference in education
        </p>
      </div>
    </div>

    <!-- Search & Filter -->
    <div class="container mt-5">
      <div class="text-start mb-4">
        <h4 class="fw-medium">Be part of our mission</h4>
        <p class="text-muted">
          We're looking for passionate people to join us on our mission. We
          value diversity, creativity, and a commitment to excellence. Explore
          our open positions and find the right fit for you.
        </p>
      </div>

      <div class="row mb-4">
        <div class="col-md-8 mb-3 mb-md-0">
          <div class="input-group">
            <span class="input-group-text bg-white border-end-0">
              <i class="bi bi-search"></i>
            </span>
            <input
              type="text"
              class="form-control border-start-0"
              placeholder="Search positions by title, department, or tag..."
              v-model="searchQuery"
            />
          </div>
        </div>
        <div class="col-md-4">
          <select class="form-select" v-model="selectedType">
            <option value="">All Employment Types</option>
            <option v-for="type in employmentTypes" :key="type" :value="type">
              {{ type }}
            </option>
          </select>
        </div>
      </div>

      <!-- Tags Filter -->
      <div class="mb-4" v-if="allTags.length > 0">
        <button
          v-for="tag in allTags"
          :key="tag"
          class="btn btn-sm btn-outline-secondary me-2 mb-2 rounded-3"
          :class="{ active: searchQuery === tag }"
          @click="searchQuery = searchQuery === tag ? '' : tag"
        >
          {{ tag }}
        </button>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-3 text-muted">Loading open positions...</p>
      </div>

      <!-- No Jobs -->
      <div v-else-if="filteredJobs.length === 0" class="text-center py-5">
        <i class="bi bi-briefcase fs-1 text-muted"></i>
        <h5 class="mt-3 text-muted">
          {{
            jobs.length === 0
              ? "No open positions at the moment"
              : "No positions match your search"
          }}
        </h5>
        <p class="text-muted">
          {{
            jobs.length === 0
              ? "Please check back later for new opportunities."
              : "Try adjusting your search or filters."
          }}
        </p>
        <button
          v-if="searchQuery || selectedType"
          class="btn btn-outline-primary btn-sm"
          @click="
            searchQuery = '';
            selectedType = '';
          "
        >
          Clear Filters
        </button>
      </div>

      <!-- Job Listings -->
      <div v-else class="mb-5">
        <p class="text-muted mb-3">
          {{ filteredJobs.length }} open position{{
            filteredJobs.length !== 1 ? "s" : ""
          }}
        </p>
        <div class="row">
          <div v-for="job in filteredJobs" :key="job._id" class="mb-4">
            <div
              class="d-flex justify-content-between align-items-center border-bottom pb-3"
            >
              <div class="text-start">
                <h6 class="fw-semibold">{{ job.title }}</h6>
                <p class="text-muted small">
                  {{ job.shortDescription }}
                </p>
                <div class="d-flex align-items-center mt-2">
                  <span
                    class="badge border border-secondary-subtle rounded-1 me-2 text-secondary px-2"
                  >
                    <i class="bi bi-clock"></i> {{ job.employmentType }}
                  </span>
                  <span
                    class="badge border border-secondary-subtle rounded-1 text-secondary px-2"
                  >
                    <i
                      :class="'bi ' + getLocationIcon(job.location) + ' me-1'"
                      :title="job.location"
                    ></i>
                    {{ job.location }}</span
                  >
                </div>
              </div>
              <div class="float-end">
                <router-link
                  :to="`/careers/${job.jobId}`"
                  class="fw-bold text-decoration-none mt-auto"
                >
                  Apply <i class="bi bi-arrow-up-right ms-1"></i>
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- CTA -->
    <div class="container-fluid bg-light py-3">
      <CTA
        heading="Don't see a role that fits?"
        message="We're always looking for talented individuals. Send us your resume and we'll keep you in mind for future opportunities."
        link="/contact"
        button="Contact Us"
        v-scrollanimation.once="'zoomIn'"
      />
    </div>
  </div>
</template>

<style scoped>
.bg-image-overlay {
  position: relative;
  background-image: linear-gradient(
    135deg,
    #0e1438 0%,
    #1a2a6c 50%,
    #0e1438 100%
  );
  background-size: cover;
  background-position: center;
  height: 50vh;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: white;
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(14, 20, 56, 0.7);
  z-index: 1;
}

.content {
  position: relative;
  z-index: 2;
}

.job-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border-radius: 12px;
}

.job-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1) !important;
}
</style>
